// CompaniesList.jsx - DESKTOP VIEW (with small height modal & company search)
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanies } from "../features/companiesSlice"; // Adjust path if needed
import Select, { components } from "react-select";
import { Tooltip } from "react-tooltip"; // Ensure react-tooltip is installed
import {
  onAuthStateChanged,
  signOut,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { auth } from "../firebase"; // Adjust path if needed
import SignInModal from "./SignInModal";
import "./CompaniesList.css"; // Import the DESKTOP CSS file
import { createPortal } from 'react-dom'; // Needed for modal menu portal

// --- Constants ---
const SMALL_HEIGHT_THRESHOLD = 700; // px - Adjust as needed
const DEBOUNCE_DELAY = 150; // ms for resize listener

function CompaniesList() {
  const dispatch = useDispatch();
  const { companies, loading, error } = useSelector((state) => state.companies);
  const tableContainerRef = useRef(null);
  const tableBodyRef = useRef(null);
  const userMenuRef = useRef(null);
  const userAvatarButtonRef = useRef(null);
  const filterToggleButtonRef = useRef(null);
  const filterModalRef = useRef(null);

  // --- State variables ---
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [useSmallHeightFilters, setUseSmallHeightFilters] = useState(
    typeof window !== 'undefined' ? window.innerHeight < SMALL_HEIGHT_THRESHOLD : false
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Filter States
  const [selectedInvestors, setSelectedInvestors] = useState([]);
  const [selectedStage, setSelectedStage] = useState([]);
  const [selectedExitRisk, setSelectedExitRisk] = useState([]);
  const [selectedRating, setSelectedRating] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedType, setSelectedType] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectedOtherFilters, setSelectedOtherFilters] = useState([]);

  // Sort states
  const [employeeSortDirection, setEmployeeSortDirection] = useState(null);
  const [ratingSortDirection, setRatingSortDirection] = useState(null);

  // --- Effects ---
  useEffect(() => {
    let debounceTimeoutId = null;
    const handleResize = () => {
      clearTimeout(debounceTimeoutId);
      debounceTimeoutId = setTimeout(() => {
        setUseSmallHeightFilters(window.innerHeight < SMALL_HEIGHT_THRESHOLD);
      }, DEBOUNCE_DELAY);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => { clearTimeout(debounceTimeoutId); window.removeEventListener("resize", handleResize); };
  }, []);

  useEffect(() => {
    if (!auth) { console.warn("Firebase auth is not initialized yet."); setIsAuthLoading(false); return; }
    if (!isAuthLoading && isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) { email = window.prompt('Please provide your email to complete sign-in:'); }
      if (email) {
        signInWithEmailLink(auth, email, window.location.href)
          .then((result) => {
            console.log("Signed in via email link:", result.user);
            window.localStorage.removeItem('emailForSignIn');
            window.history.replaceState({}, document.title, window.location.pathname);
          })
          .catch((error) => {
            console.error("Error signing in with email link:", error);
            window.localStorage.removeItem('emailForSignIn');
            window.history.replaceState({}, document.title, window.location.pathname);
            alert(`Error during sign-in: ${error.message}. Please try signing in normally.`);
          });
      } else {
        alert("Email required to complete sign-in.");
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [isAuthLoading]);

  useEffect(() => {
    if (!auth) { setIsAuthLoading(false); return; }
    setIsAuthLoading(true);
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsAuthLoading(false);
      if (currentUser) setShowSignInModal(false);
      else setShowUserMenu(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    dispatch(fetchCompanies());
  }, [dispatch]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        userAvatarButtonRef.current &&
        !userAvatarButtonRef.current.contains(event.target)
      ) {
        setShowUserMenu(false);
      }
    }
    if (showUserMenu) document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeFilterModal();
      if (event.key === 'Tab' && filterModalRef.current) {
        const focusable = filterModalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const [first, last] = [focusable[0], focusable[focusable.length - 1]];
        if (event.shiftKey) {
          if (document.activeElement === first) { last.focus(); event.preventDefault(); }
        } else {
          if (document.activeElement === last) { first.focus(); event.preventDefault(); }
        }
      }
    };
    const origOverflow = document.body.style.overflow;
    if (isFilterModalOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
      setTimeout(() => {
        filterModalRef.current?.querySelector('.filter-modal-close-button')?.focus();
      }, 100);
    } else {
      document.body.style.overflow = origOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = origOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFilterModalOpen]);

  const parseEmployeeValue = (emp) => {
    if (!emp) return 0;
    if (typeof emp === "number") return emp;
    const s = String(emp).toLowerCase().replace(/,/g, '');
    if (s.includes("k+")) return parseInt(s.replace("k+", "000"), 10);
    if (s.includes("-")) return parseInt(s.split("-")[0], 10);
    return parseInt(s, 10) || 0;
  };
  const parseRatingValue = (rating) => {
    if (rating == null) return -1;
    if (typeof rating === "number") return rating;
    const map = { 'A+':12,'A':11,'A-':10,'B+':9,'B':8,'B-':7,'C+':6,'C':5,'C-':4,'D+':3,'D':2,'D-':1,'F':0 };
    const s = String(rating).toUpperCase().trim();
    return map[s] ?? (s==='N/A'||s==='' ? -1 : -1);
  };
  const toggleEmployeeSort = useCallback(() => {
    setEmployeeSortDirection(prev => prev==="asc"?"desc":prev==="desc"?null:"asc");
    setRatingSortDirection(null);
  }, []);
  const toggleRatingSort = useCallback(() => {
    setRatingSortDirection(prev => prev==="asc"?"desc":prev==="desc"?null:"asc");
    setEmployeeSortDirection(null);
  }, []);
  const getInitials = useCallback((disp) => {
    if (!user) return "?";
    const name = disp || user.email || "";
    if (name.includes('@')) return name[0].toUpperCase();
    const parts = name.split(" ").filter(Boolean);
    if (!parts.length) return "?";
    return (parts[0][0] + (parts[parts.length-1]||"")[0]).toUpperCase();
  }, [user]);
  const handleLogout = async () => {
    try {
      if(auth) await signOut(auth);
      setShowUserMenu(false);
    } catch (err) {
      console.error("Sign out error:", err);
      alert(`Failed to sign out: ${err.message}`);
    }
  };
  const handleRowClick = useCallback((companyName) => {
    if (!companyName || companyName==='N/A') return;
    const safe = String(companyName).toLowerCase()
      .replace(/\s+/g,'-')
      .replace(/[^a-z0-9-]/g,'')
      .replace(/-+/g,'-')
      .replace(/^-+|-+$/g,'');
    if (safe) window.location.href = `/${safe}-summary.html`;
  }, []);
  const handleViewReportClick = useCallback((e, companyName) => {
    e.stopPropagation();
    handleRowClick(companyName);
  }, [handleRowClick]);

  const closeFilterModal = useCallback(() => {
    setIsFilterModalOpen(false);
    requestAnimationFrame(() => filterToggleButtonRef.current?.focus());
  }, []);

  const clearAllFilters = () => {
    setSelectedInvestors([]); setSelectedStage([]); setSelectedExitRisk([]);
    setSelectedRating([]); setSelectedIndustry([]); setSelectedLocation([]);
    setSelectedType([]); setSelectedEmployees([]); setSelectedNames([]);
    setSelectedOtherFilters([]);
  };

  const sortOptions = (arr) => [...new Set(arr)]
    .filter(i=>i!=null && i!=='')
    .map(item=>({value:item,label:String(item)}))
    .sort((a,b)=>String(a.label).localeCompare(String(b.label),undefined,{numeric:true}));

  const uniqueInvestors = useMemo(()=>companies?sortOptions(companies.flatMap(c=>c?.investors||[]).filter(Boolean)):[],[companies]);
  const uniqueExitRisks = useMemo(()=>companies?sortOptions(companies.map(c=>c?.exit_risk)):[],[companies]);
  const uniqueRatings = useMemo(()=>companies?sortOptions(companies.map(c=>c?.rating)):[],[companies]);
  const stageOptions = useMemo(()=>[
    {value:"Seed",label:"Seed"},{value:"Series A",label:"Series A"},
    {value:"Series B",label:"Series B"},{value:"Series C",label:"Series C"},
    {value:"Series D",label:"Series D"},{value:"Series E or Later",label:"Series E or Later"},
    {value:"N/A",label:"N/A"}].sort((a,b)=>a.label.localeCompare(b.label)),[]);
  const uniqueIndustries = useMemo(()=>companies?sortOptions(companies.map(c=>c?.industry)):[],[companies]);
  const uniqueLocations = useMemo(()=>companies?sortOptions(companies.map(c=>c?.location)):[],[companies]);
  const uniqueTypes = useMemo(()=>companies?sortOptions(companies.map(c=>c?.type)):[],[companies]);
  const uniqueEmployees = useMemo(()=>companies?sortOptions(companies.map(c=>c?.employees)):[],[companies]);
  const uniqueNames = useMemo(()=>companies?sortOptions(companies.map(c=>c?.name)):[],[companies]);
  const otherOptions = useMemo(()=>[
    {value:"early_stage",label:"Early Stage"},
    {value:"recent_fund_raise",label:"Recent Fund Raise"},
    {value:"female_founder",label:"Female Founder"},
    {value:"repeat_unicorn_founder",label:"Repeat Unicorn Founder"}
  ],[]);

  const activeFilterCount = useMemo(()=>{
    const lists = [
      selectedInvestors, selectedStage, selectedExitRisk, selectedRating,
      selectedIndustry, selectedLocation, selectedType, selectedEmployees,
      selectedNames, selectedOtherFilters
    ];
    return lists.reduce((sum, arr) => sum + (arr.length>0?1:0), 0);
  },[
    selectedInvestors, selectedStage, selectedExitRisk, selectedRating,
    selectedIndustry, selectedLocation, selectedType, selectedEmployees,
    selectedNames, selectedOtherFilters
  ]);

  const filteredCompanies = useMemo(()=>{
    if (!Array.isArray(companies)) return [];
    return companies.filter(c=>{
      if (!c) return false;
      const {
        investors = [], stage="N/A", exit_risk="N/A", rating="N/A",
        industry="N/A", location="N/A", type="N/A", employees="N/A",
        name="N/A", early_stage=false, recent_fund_raise=false,
        female_founder=false, repeat_unicorn_founder=false
      } = c;
      const match = (field, sel)=>{
        if (!sel.length) return true;
        return sel.some(s=>String(s.value).toLowerCase().trim()===String(field).toLowerCase().trim());
      };
      const investorsMatch = !selectedInvestors.length
        || selectedInvestors.some(si=>investors.map(inv=>inv.toLowerCase()).includes(si.value.toLowerCase()));
      const otherMatch = !selectedOtherFilters.length || selectedOtherFilters.every(f=>{
        switch(f.value){
          case "early_stage": return early_stage;
          case "recent_fund_raise": return recent_fund_raise;
          case "female_founder": return female_founder;
          case "repeat_unicorn_founder": return repeat_unicorn_founder;
          default: return true;
        }
      });
      return investorsMatch
        && match(stage, selectedStage)
        && match(exit_risk, selectedExitRisk)
        && match(rating, selectedRating)
        && match(industry, selectedIndustry)
        && match(location, selectedLocation)
        && match(type, selectedType)
        && match(employees, selectedEmployees)
        && match(name, selectedNames)
        && otherMatch;
    });
  },[
    companies, selectedInvestors, selectedStage, selectedExitRisk,
    selectedRating, selectedIndustry, selectedLocation, selectedType,
    selectedEmployees, selectedNames, selectedOtherFilters
  ]);

  const sortedCompanies = useMemo(()=>{
    const arr = [...filteredCompanies];
    return arr.sort((a,b)=>{
      if (ratingSortDirection) {
        const diff = ratingSortDirection==="asc"
          ? parseRatingValue(a.rating) - parseRatingValue(b.rating)
          : parseRatingValue(b.rating) - parseRatingValue(a.rating);
        if (diff) return diff;
      }
      if (employeeSortDirection) {
        const diff = employeeSortDirection==="asc"
          ? parseEmployeeValue(a.employees) - parseEmployeeValue(b.employees)
          : parseEmployeeValue(b.employees) - parseEmployeeValue(a.employees);
        if (diff) return diff;
      }
      return String(a.name||"").localeCompare(String(b.name||""));
    });
  },[filteredCompanies, ratingSortDirection, employeeSortDirection]);

  const blurLastTwo = !isAuthLoading && !user && sortedCompanies.length > 3;

  const CustomOption = useCallback(({ innerRef, innerProps, isFocused, isSelected, data })=>(
    <div
      ref={innerRef}
      {...innerProps}
      className={`custom-option ${isSelected?"selected":""} ${isFocused?"focused":""}`}
      style={{cursor:'pointer'}}
    >
      <span className="option-checkbox">{isSelected?"✔":""}</span>
      <span style={{marginLeft:'8px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
        {data.label}
      </span>
    </div>
  ),[]);

  const CustomMultiValue = useCallback((props)=>{
    const { index, selectProps } = props;
    const { value } = selectProps;
    const MAX_VISIBLE = 1;
    if (index===0) return <components.MultiValue {...props}/>;
    if (index===MAX_VISIBLE && Array.isArray(value) && value.length>MAX_VISIBLE) {
      const remaining = value.length - MAX_VISIBLE;
      const hidden = value.slice(MAX_VISIBLE).map(i=>i.label).join(", ");
      return (
        <div
          className="react-select__multi-value react-select__multi-value--more"
          data-tooltip-id="global-select-tooltip"
          data-tooltip-content={hidden}
        >
          +{remaining} more
        </div>
      );
    }
    return null;
  },[]);

  const buildSelectProps = useCallback((key, value, onChange, options, placeholder, isMulti=true)=>({
    value, onChange, options, isMulti, instanceId:key,
    closeMenuOnSelect:!isMulti, hideSelectedOptions:false,
    placeholder, classNamePrefix:"react-select",
    components:{Option:CustomOption,MultiValue:CustomMultiValue,IndicatorSeparator:null},
    menuPortalTarget: typeof window!=="undefined"?document.body:null,
    styles:{menuPortal:base=>({...base,zIndex:1050})},
    menuShouldBlockScroll:true,
    'aria-label':placeholder
  }),[CustomOption,CustomMultiValue]);

  const commonModalSelectProps = useMemo(()=>({
    isMulti:true, className:"modal-select", classNamePrefix:"react-select",
    menuPortalTarget:typeof document!=="undefined"?document.body:null,
    styles:{
      menuPortal:base=>({...base,zIndex:10101}),
      menu:base=>({...base,menuPlacement:'auto',width:'auto',minWidth:'100%'})
    },
    menuPlacement:'auto', closeMenuOnSelect:false,
    hideSelectedOptions:false, blurInputOnSelect:false,
    isSearchable:true, menuShouldBlockScroll:true,
    components:{MultiValue:CustomMultiValue,IndicatorSeparator:null}
  }),[CustomMultiValue]);

  const smallBarSelectProps = useMemo(()=>({
    isMulti:true, instanceId:"small-bar-name-select",
    options:uniqueNames, value:selectedNames, onChange:setSelectedNames,
    placeholder:"Search companies...", className:"small-bar-select",
    classNamePrefix:"react-select", closeMenuOnSelect:false,
    hideSelectedOptions:false, blurInputOnSelect:false,
    isSearchable:true,
    menuPortalTarget:typeof window!=="undefined"?document.body:null,
    styles:{
      menuPortal:base=>({...base,zIndex:1005}),
      control:base=>({...base,minHeight:'40px',height:'40px'}),
      valueContainer:base=>({...base,height:'40px',padding:'0 8px'}),
      indicatorsContainer:base=>({...base,height:'40px'})
    },
    menuShouldBlockScroll:true, 'aria-label':"Search Companies",
    components:{MultiValue:CustomMultiValue,IndicatorSeparator:null}
  }),[uniqueNames,selectedNames,CustomMultiValue]);

  // --- Render ---
  if (isAuthLoading || (loading && !companies?.length)) {
    return <div className="loading-state">Loading...</div>;
  }
  if (error) {
    const msg = typeof error==='string'?error:JSON.stringify(error);
    return <div className="error-state">Error loading companies: {msg}</div>;
  }

  const layoutClass = `companies-layout-container ${useSmallHeightFilters?'has-modal-filter-bar':''}`;

  return (
    <div className={layoutClass}>

      {/* NAV */}
      <nav className="app-nav">
        <div className="app-branding">
          <a href="/" aria-label="Go to homepage" className="app-logo-link">
            <img
              src="/logo.png"
              alt="Logo"
              className="app-logo"
              onError={(e)=>e.target.style.display='none'}
            />
          </a>
        </div>
        
        {/* Top Ad Unit */}
        <div className="header-ad-container">
          <ins className="adsbygoogle"
               style={{display:'block'}}
               data-ad-client="ca-pub-6950549355554638"
               data-ad-slot="7867689560"
               data-ad-format="auto"
               data-full-width-responsive="true"></ins>
        </div>
        
        <div className="user-controls">
          {isAuthLoading ? (
            <div
              className="loading-placeholder"
              style={{color:'white',padding:'10px 15px'}}
            >
              Loading...
            </div>
          ) : user ? (
            <div style={{position:'relative'}} ref={userAvatarButtonRef}>
              <div
                className="user-control-area"
                onClick={()=>setShowUserMenu(!showUserMenu)}
                title="User menu"
                role="button"
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded={showUserMenu}
                onKeyDown={e=>(e.key==='Enter'||e.key===' ')&&setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">{getInitials(user.displayName)}</div>
              </div>
              {showUserMenu && (
                <div className="user-menu" ref={userMenuRef}>
                  <div
                    className="user-menu-email"
                    title={user.email||'N/A'}
                  >
                    {user.email||'N/A'}
                  </div>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          ) : (
            <button
              className="header-sign-in-button"
              onClick={()=>setShowSignInModal(true)}
            >
              Sign In
            </button>
          )}
        </div>
      </nav>

      {/* FILTER BAR */}
      {useSmallHeightFilters ? (
        <div className="desktop-modal-filter-bar">
          <div className="company-name-filter-wrapper">
            <Select {...smallBarSelectProps} />
          </div>
          <button
            ref={filterToggleButtonRef}
            className="filter-toggle-button"
            onClick={()=>setIsFilterModalOpen(true)}
            aria-label={`Open Filters${activeFilterCount>0?` (${activeFilterCount})`:''}`}
            aria-haspopup="dialog"
            aria-expanded={isFilterModalOpen}
            aria-controls="filter-modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16" height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="filter-icon"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
            {activeFilterCount > 0 && (
              <span className="filter-count-badge" aria-hidden="true">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      ) : (
        <div className="filters-top-container">
          {/* repeat for each filter */}
          <div className="filter-item">
            <label htmlFor="investors-select">Investors</label>
            <Select
              inputId="investors-select"
              {...buildSelectProps(
                "investors",
                selectedInvestors,
                setSelectedInvestors,
                uniqueInvestors,
                "Investors..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="stage-select">Stage</label>
            <Select
              inputId="stage-select"
              {...buildSelectProps(
                "stage",
                selectedStage,
                setSelectedStage,
                stageOptions,
                "Stage..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="exit-risk-select">Exit Risk</label>
            <Select
              inputId="exit-risk-select"
              {...buildSelectProps(
                "exitRisk",
                selectedExitRisk,
                setSelectedExitRisk,
                uniqueExitRisks,
                "Exit Risk..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="rating-select">Rating</label>
            <Select
              inputId="rating-select"
              {...buildSelectProps(
                "rating",
                selectedRating,
                setSelectedRating,
                uniqueRatings,
                "Rating..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="industry-select">Industry</label>
            <Select
              inputId="industry-select"
              {...buildSelectProps(
                "industry",
                selectedIndustry,
                setSelectedIndustry,
                uniqueIndustries,
                "Industry..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="location-select">Location</label>
            <Select
              inputId="location-select"
              {...buildSelectProps(
                "location",
                selectedLocation,
                setSelectedLocation,
                uniqueLocations,
                "Location..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="type-select">Type</label>
            <Select
              inputId="type-select"
              {...buildSelectProps(
                "type",
                selectedType,
                setSelectedType,
                uniqueTypes,
                "Type..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="employees-select">Employees</label>
            <Select
              inputId="employees-select"
              {...buildSelectProps(
                "employees",
                selectedEmployees,
                setSelectedEmployees,
                uniqueEmployees,
                "Employees..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="name-select">Company Name</label>
            <Select
              inputId="name-select"
              {...buildSelectProps(
                "name",
                selectedNames,
                setSelectedNames,
                uniqueNames,
                "Company Name..."
              )}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="other-select">Other Filters</label>
            <Select
              inputId="other-select"
              {...buildSelectProps(
                "other",
                selectedOtherFilters,
                setSelectedOtherFilters,
                otherOptions,
                "Other Filters..."
              )}
            />
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className="table-content">
        <Tooltip
          id="global-select-tooltip"
          place="top"
          className="tooltip-base-style"
          style={{ zIndex: 1051 }}
        />

        <div className="table-container" ref={tableContainerRef}>
          <div className="table-horizontal-scroll-wrapper">
            <table className="companies-table">
              <thead>
                <tr>
                  <th className="sticky-col-left fixed-col-company header-sticky">
                    Name
                  </th>
                  <th className="sticky-col-left fixed-col-exitRisk header-sticky">
                    Exit Risk
                  </th>
                  <th
                    className="sticky-col-left fixed-col-rating header-sticky"
                    onClick={toggleRatingSort}
                    title={`Sort by Rating (${
                      ratingSortDirection === "asc" ? "Ascending" :
                      ratingSortDirection === "desc" ? "Descending" : "Unsorted"
                    })`}
                  >
                    Rating
                    <span className={`sort-arrow ${ratingSortDirection || "unsorted"}`}></span>
                  </th>
                  <th className="sticky-col-left fixed-col-report header-sticky">
                    Report
                  </th>
                  <th className="fixed-col-stage header-sticky">Stage</th>
                  <th className="fixed-col-tags header-sticky">Tags</th>
                  <th
                    className="fixed-col-employees header-sticky"
                    onClick={toggleEmployeeSort}
                    title={`Sort by Employees (${
                      employeeSortDirection === "asc" ? "Ascending" :
                      employeeSortDirection === "desc" ? "Descending" : "Unsorted"
                    })`}
                  >
                    Employees
                    <span className={`sort-arrow ${employeeSortDirection || "unsorted"}`}></span>
                  </th>
                  <th className="fixed-col-industry header-sticky">Industry</th>
                  <th className="fixed-col-investors header-sticky">Investors</th>
                  <th className="fixed-col-primary header-sticky">Primary Office</th>
                  <th className="fixed-col-type header-sticky">Type</th>
                </tr>
              </thead>
              <tbody
                ref={tableBodyRef}
                className={`companies-table-body ${blurLastTwo?"blur-last-two":""}`}
              >
                {sortedCompanies.length > 0 ? (
                  sortedCompanies.map((company, idx) => {
                    const {
                      id, name="N/A", exit_risk="N/A", rating="N/A",
                      stage="N/A", employees="N/A", industry="N/A",
                      investors = [], location="N/A", type="N/A",
                      early_stage=false, recent_fund_raise=false,
                      female_founder=false, repeat_unicorn_founder=false
                    } = company||{};
                    const key = id || `${name}-${idx}`;
                    const isTruthy = val => !['false','0','no','',null,undefined]
                      .includes(String(val).toLowerCase().trim());
                    const tags = [];
                    if (isTruthy(early_stage)) tags.push("Early Stage");
                    if (isTruthy(recent_fund_raise)) tags.push("Recent Fund Raise");
                    if (isTruthy(female_founder)) tags.push("Female Founded");
                    if (isTruthy(repeat_unicorn_founder)) tags.push("Repeat Unicorn Founder");
                    const MAX_TAGS = 2;
                    const showTags = tags.slice(0, MAX_TAGS);
                    const hiddenTagCount = tags.length - showTags.length;
                    const tagTooltip = hiddenTagCount>0
                      ? tags.slice(MAX_TAGS).join(", ")
                      : "";
                    const tagId = `tags-tooltip-${key.replace(/[^a-z0-9-]/gi,'')}`;

                    const validInv = investors.filter(Boolean);
                    const MAX_INV = 1;
                    const showInv = validInv.slice(0, MAX_INV);
                    const hiddenInvCount = validInv.length - showInv.length;
                    const invTooltip = hiddenInvCount>0
                      ? validInv.slice(MAX_INV).join("\n")
                      : "";
                    const invId = `inv-tooltip-${key.replace(/[^a-z0-9-]/gi,'')}`;

                    return (
                      <tr
                        key={key}
                        onClick={()=>handleRowClick(name)}
                        className="company-row"
                        style={{cursor:name&&name!=='N/A'?'pointer':'default'}}
                        tabIndex={0}
                        onKeyDown={e=>(e.key==='Enter'||e.key===' ')&&handleRowClick(name)}
                        role="row"
                      >
                        <td className="sticky-col-left fixed-col-company" role="cell">
                          {name}
                        </td>
                        <td className="sticky-col-left fixed-col-exitRisk" role="cell">
                          {exit_risk}
                        </td>
                        <td className="sticky-col-left fixed-col-rating" role="cell">
                          {rating}
                        </td>
                        <td
                          className="sticky-col-left fixed-col-report action-cell-container"
                          role="cell"
                        >
                          <button
                            className="view-report-button"
                            onClick={(e)=>handleViewReportClick(e,name)}
                            title={`View report for ${name}`}
                            disabled={!name||name==='N/A'}
                            aria-label={`View report for ${name}`}
                          >
                            View Report
                          </button>
                        </td>
                        <td className="fixed-col-stage" role="cell">{stage}</td>
                        <td className="fixed-col-tags" role="cell">
                          <div className="pill-container">
                            {showTags.map(tag=>(
                              <span key={tag} className="pill tag-pill">{tag}</span>
                            ))}
                            {hiddenTagCount>0 && (
                              <>
                                <span
                                  className="pill more-pill"
                                  data-tooltip-id={tagId}
                                  data-tooltip-content={tagTooltip}
                                  aria-label={`${hiddenTagCount} more tags: ${tagTooltip}`}
                                >
                                  +{hiddenTagCount} more
                                </span>
                                <Tooltip
                                  id={tagId}
                                  place="top"
                                  className="tooltip-base-style"
                                  style={{zIndex:1051}}
                                />
                              </>
                            )}
                            {!tags.length && "N/A"}
                          </div>
                        </td>
                        <td className="fixed-col-employees" role="cell">{employees}</td>
                        <td className="fixed-col-industry" role="cell">{industry}</td>
                        <td className="fixed-col-investors" role="cell">
                          <div className="pill-container">
                            {showInv.map(inv=>(
                              <span key={inv} className="pill investor-pill">{inv}</span>
                            ))}
                            {hiddenInvCount>0 && (
                              <>
                                <span
                                  className="pill more-pill"
                                  data-tooltip-id={invId}
                                  data-tooltip-content={invTooltip}
                                  aria-label={`${hiddenInvCount} more investors`}
                                >
                                  +{hiddenInvCount} more
                                </span>
                                <Tooltip
                                  id={invId}
                                  place="top"
                                  className="tooltip-base-style multiline-tooltip"
                                  style={{zIndex:1051}}
                                />
                              </>
                            )}
                            {!validInv.length && "N/A"}
                          </div>
                        </td>
                        <td className="fixed-col-primary" role="cell">{location}</td>
                        <td className="fixed-col-type" role="cell">{type}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr role="row">
                    <td
                      colSpan={11}
                      style={{
                        textAlign:"center",
                        padding:"40px 0",
                        color:"#6c757d",
                        fontStyle:"italic"
                      }}
                      role="cell"
                    >
                      No companies match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* SIGNUP PROMPT */}
      {!isAuthLoading && !user && !useSmallHeightFilters && (
        <>
          {/* Ad Unit - Before Signup Prompt */}
          <div style={{ textAlign: 'center', margin: '2rem 0', padding: '1rem' }}>
            <ins className="adsbygoogle"
                 style={{display:'block'}}
                 data-ad-client="ca-pub-6950549355554638"
                 data-ad-slot="1234567890"
                 data-ad-format="auto"
                 data-full-width-responsive="true"></ins>
          </div>

          <div className="signup-prompt">
            <p>Sign in or create account to continue browsing</p>
            <p className="signup-note">Summary reports are 100% free</p>
            <button
              className="sign-in-button"
              onClick={()=>setShowSignInModal(true)}
            >
              Sign in
            </button>
          </div>
        </>
      )}

      {/* SIGN IN MODAL */}
      {showSignInModal && <SignInModal onClose={()=>setShowSignInModal(false)} />}

      {/* FILTER MODAL */}
      {isFilterModalOpen && createPortal(
        <div
          className="filter-modal-overlay"
          onClick={closeFilterModal}
          role="presentation"
        >
          <div
            ref={filterModalRef}
            className="filter-modal"
            id="filter-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-modal-title"
            onClick={e=>e.stopPropagation()}
          >
            <div className="filter-modal-header">
              <h2 id="filter-modal-title" className="filter-modal-title">Filters</h2>
              <button
                className="filter-modal-close-button"
                onClick={closeFilterModal}
                aria-label="Close Filters Modal"
              >×</button>
            </div>
            <div className="filter-modal-content">
              <div className="filter-item">
                <label htmlFor="modal-investors-select">Investors</label>
                <Select
                  inputId="modal-investors-select"
                  {...commonModalSelectProps}
                  options={uniqueInvestors}
                  value={selectedInvestors}
                  onChange={setSelectedInvestors}
                  placeholder="Select investors..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-stage-select">Stage</label>
                <Select
                  inputId="modal-stage-select"
                  {...commonModalSelectProps}
                  options={stageOptions}
                  value={selectedStage}
                  onChange={setSelectedStage}
                  placeholder="Select stages..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-exit-risk-select">Exit Risk</label>
                <Select
                  inputId="modal-exit-risk-select"
                  {...commonModalSelectProps}
                  options={uniqueExitRisks}
                  value={selectedExitRisk}
                  onChange={setSelectedExitRisk}
                  placeholder="Select exit risks..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-rating-select">Rating</label>
                <Select
                  inputId="modal-rating-select"
                  {...commonModalSelectProps}
                  options={uniqueRatings}
                  value={selectedRating}
                  onChange={setSelectedRating}
                  placeholder="Select ratings..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-industry-select">Industry</label>
                <Select
                  inputId="modal-industry-select"
                  {...commonModalSelectProps}
                  options={uniqueIndustries}
                  value={selectedIndustry}
                  onChange={setSelectedIndustry}
                  placeholder="Select industries..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-location-select">Location</label>
                <Select
                  inputId="modal-location-select"
                  {...commonModalSelectProps}
                  options={uniqueLocations}
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  placeholder="Select locations..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-type-select">Type</label>
                <Select
                  inputId="modal-type-select"
                  {...commonModalSelectProps}
                  options={uniqueTypes}
                  value={selectedType}
                  onChange={setSelectedType}
                  placeholder="Select types..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-employees-select">Employees</label>
                <Select
                  inputId="modal-employees-select"
                  {...commonModalSelectProps}
                  options={uniqueEmployees}
                  value={selectedEmployees}
                  onChange={setSelectedEmployees}
                  placeholder="Select employee ranges..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-name-select">Company Name</label>
                <Select
                  inputId="modal-name-select"
                  {...commonModalSelectProps}
                  options={uniqueNames}
                  value={selectedNames}
                  onChange={setSelectedNames}
                  placeholder="Select company names..."
                />
              </div>
              <div className="filter-item">
                <label htmlFor="modal-other-select">Other Filters</label>
                <Select
                  inputId="modal-other-select"
                  {...commonModalSelectProps}
                  options={otherOptions}
                  value={selectedOtherFilters}
                  onChange={setSelectedOtherFilters}
                  placeholder="Select other filters..."
                />
              </div>
            </div>
            <div className="filter-modal-footer">
              <button
                className="modal-clear-button"
                onClick={clearAllFilters}
                disabled={activeFilterCount===0}
              >
                Clear All ({activeFilterCount})
              </button>
              <button
                className="modal-action-button"
                onClick={closeFilterModal}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default CompaniesList;
